import React, { useState } from 'react';
import { SidebarLayout, PageHeader, Card } from '../components/layout';
import { useAuth } from '../hooks/use-auth';
import { useUserMatchIdentifiers } from '../hooks/use-user-profile';
import { useUserDivisions } from '../hooks/use-user-divisions';
import { useQuery } from '@tanstack/react-query';
import { UserService, UserActivityDto, UserMatchIdentifierDto, UserDto } from '../services/user-service';
import httpClient from '../services/http-client';
import { User, Mail, Building, Calendar, Activity, Shield, Tag, Search, CheckCircle, Workflow } from 'lucide-react';
import { format } from 'date-fns';

const userService = new UserService(httpClient);

export function ProfilePage() {
  const { user, isAuthenticated, tokenClaims, roles = [] } = useAuth();
  const [activeTab, setActiveTab] = useState<'profile' | 'activity' | 'divisions' | 'match-identifiers'>('profile');

  // Extract user information from token claims
  const email = user?.profile?.email || tokenClaims?.email;
  const firstName = user?.profile?.given_name || tokenClaims?.given_name || '';
  const lastName = user?.profile?.family_name || tokenClaims?.family_name || '';
  const username = user?.profile?.preferred_username || tokenClaims?.preferred_username;

  // Get division information from API instead of token
  const { data: userDivisionsData } = useUserDivisions();
  const divisions = userDivisionsData?.divisions || [];
  const divisionRoles = userDivisionsData?.divisionRoles || [];
  const primaryDivisionId = divisions[0]?.id;

  // Fetch current user's profile from the API to get their internal user ID
  const { data: currentUser, isLoading: currentUserLoading } = useQuery<UserDto>({
    queryKey: ['current-user'],
    queryFn: () => userService.getCurrentUser(),
    enabled: isAuthenticated,
    retry: false,
  });

  // Use the internal user ID from the API response
  const internalUserId = currentUser?.id;

  // Fetch user activity if user ID is available
  const { data: userActivity, isLoading: activityLoading } = useQuery({
    queryKey: ['user-activity', internalUserId],
    queryFn: async (): Promise<UserActivityDto | null> => {
      if (!internalUserId) return null;
      try {
        return await userService.getUserActivity(internalUserId);
      } catch (error) {
        console.error('Error fetching user activity:', error);
        return null;
      }
    },
    enabled: isAuthenticated && !!internalUserId,
    retry: false,
  });

  // Fetch user match identifiers for primary division
  const { data: matchIdentifiersData, isLoading: matchIdentifiersLoading } = useUserMatchIdentifiers(
    primaryDivisionId,
    internalUserId
  );

  if (!isAuthenticated) {
    return (
      <SidebarLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Authentication Required
            </h2>
            <p className="text-gray-600 dark:text-secondary-400">
              Please log in to view your profile.
            </p>
          </div>
        </div>
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout>
      <div className="py-4">
        <PageHeader
          title="My Profile"
          subtitle="View and manage your account information"
        />

        {/* Tab Navigation */}
        <div className="mb-6">
          <div className="border-b border-gray-200 dark:border-secondary-700">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('profile')}
                className={`
                  py-4 px-1 border-b-2 font-medium text-sm
                  ${activeTab === 'profile'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 dark:text-secondary-400 hover:text-gray-700 dark:hover:text-secondary-200 hover:border-gray-300 dark:hover:border-secondary-500'
                  }
                `}
              >
                Profile Information
              </button>
              <button
                onClick={() => setActiveTab('divisions')}
                className={`
                  py-4 px-1 border-b-2 font-medium text-sm
                  ${activeTab === 'divisions'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 dark:text-secondary-400 hover:text-gray-700 dark:hover:text-secondary-200 hover:border-gray-300 dark:hover:border-secondary-500'
                  }
                `}
              >
                Divisions & Roles
              </button>
              <button
                onClick={() => setActiveTab('match-identifiers')}
                className={`
                  py-4 px-1 border-b-2 font-medium text-sm
                  ${activeTab === 'match-identifiers'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 dark:text-secondary-400 hover:text-gray-700 dark:hover:text-secondary-200 hover:border-gray-300 dark:hover:border-secondary-500'
                  }
                `}
              >
                Match Identifiers
                {matchIdentifiersData && matchIdentifiersData.activeCount > 0 && (
                  <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                    {matchIdentifiersData.activeCount}
                  </span>
                )}
              </button>
              <button
                onClick={() => setActiveTab('activity')}
                className={`
                  py-4 px-1 border-b-2 font-medium text-sm
                  ${activeTab === 'activity'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 dark:text-secondary-400 hover:text-gray-700 dark:hover:text-secondary-200 hover:border-gray-300 dark:hover:border-secondary-500'
                  }
                `}
              >
                Activity
              </button>
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <div className="space-y-6">
          {activeTab === 'profile' && (
            <ProfileInformation
              email={email}
              firstName={firstName}
              lastName={lastName}
              username={username}
              tokenClaims={tokenClaims}
            />
          )}

          {activeTab === 'divisions' && (
            <DivisionInformation
              divisions={divisions}
              divisionRoles={divisionRoles}
              systemRoles={roles}
            />
          )}

          {activeTab === 'match-identifiers' && (
            <MatchIdentifiersInformation
              matchIdentifiersData={matchIdentifiersData || null}
              isLoading={matchIdentifiersLoading}
              divisionId={primaryDivisionId}
            />
          )}

          {activeTab === 'activity' && (
            <ActivityInformation
              userActivity={userActivity || null}
              isLoading={activityLoading}
            />
          )}
        </div>
      </div>
    </SidebarLayout>
  );
}

interface ProfileInformationProps {
  email?: string;
  firstName: string;
  lastName: string;
  username?: string;
  tokenClaims: any;
}

function ProfileInformation({ email, firstName, lastName, username, tokenClaims }: ProfileInformationProps) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Basic Information */}
      <Card>
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
            <User className="h-5 w-5 mr-2 text-primary-600" />
            Basic Information
          </h3>
        </div>
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-500 dark:text-secondary-400">Full Name</label>
            <p className="mt-1 text-gray-900 dark:text-white">
              {firstName && lastName ? `${firstName} ${lastName}` : 'Not available'}
            </p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-500 dark:text-secondary-400 flex items-center">
              <Mail className="h-4 w-4 mr-1" />
              Email
            </label>
            <p className="mt-1 text-gray-900 dark:text-white">{email || 'Not available'}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-500 dark:text-secondary-400">Username</label>
            <p className="mt-1 text-gray-900 dark:text-white">{username || 'Not available'}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-500 dark:text-secondary-400">User ID</label>
            <p className="mt-1 text-gray-900 dark:text-white font-mono text-sm">{tokenClaims?.sub || 'Not available'}</p>
          </div>
        </div>
      </Card>

      {/* Authentication Information */}
      <Card>
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
            <Shield className="h-5 w-5 mr-2 text-primary-600" />
            Authentication Details
          </h3>
        </div>
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-500 dark:text-secondary-400">Session ID</label>
            <p className="mt-1 text-gray-900 dark:text-white font-mono text-sm break-all">
              {tokenClaims?.sid || 'Not available'}
            </p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-500 dark:text-secondary-400">Token Issuer</label>
            <p className="mt-1 text-gray-900 dark:text-white text-sm">{tokenClaims?.iss || 'Not available'}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-500 dark:text-secondary-400">Token Audience</label>
            <p className="mt-1 text-gray-900 dark:text-white text-sm">
              {Array.isArray(tokenClaims?.aud) 
                ? tokenClaims.aud.join(', ') 
                : tokenClaims?.aud || 'Not available'}
            </p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-500 dark:text-secondary-400">Token Expiration</label>
            <p className="mt-1 text-gray-900 dark:text-white text-sm">
              {tokenClaims?.exp 
                ? format(new Date(tokenClaims.exp * 1000), 'PPpp')
                : 'Not available'}
            </p>
          </div>
        </div>
      </Card>

      {/* Additional Claims */}
      <Card className="lg:col-span-2">
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Additional Token Claims
          </h3>
        </div>
        <div className="bg-gray-50 dark:bg-secondary-900 rounded-lg p-4">
          <pre className="text-xs text-gray-700 dark:text-secondary-200 overflow-auto max-h-96">
            {JSON.stringify(tokenClaims, null, 2)}
          </pre>
        </div>
      </Card>
    </div>
  );
}

interface DivisionInformationProps {
  divisions: Array<{ id: string; name: string; code?: string }>;
  divisionRoles: Array<{ divisionId: string; divisionName: string; role: string }>;
  systemRoles: string[];
}

function DivisionInformation({ divisions, divisionRoles, systemRoles }: DivisionInformationProps) {
  return (
    <div className="space-y-6">
      {/* System Roles */}
      <Card>
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
            <Shield className="h-5 w-5 mr-2 text-primary-600" />
            System Roles
          </h3>
        </div>
        {systemRoles.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {systemRoles.map((role, index) => (
              <span
                key={index}
                className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-100 text-primary-800"
              >
                {role}
              </span>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 dark:text-secondary-400">No system roles assigned</p>
        )}
      </Card>

      {/* Divisions & Roles */}
      <Card>
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
            <Building className="h-5 w-5 mr-2 text-primary-600" />
            Division Assignments
          </h3>
        </div>
        {divisionRoles.length > 0 ? (
          <div className="space-y-3">
            {divisionRoles.map((divRole, index) => (
              <div
                key={divRole.divisionId}
                className="p-4 border border-gray-200 dark:border-secondary-700 rounded-lg bg-gray-50 dark:bg-secondary-900"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="font-medium text-gray-900 dark:text-white">{divRole.divisionName}</p>
                    <p className="text-sm text-gray-500 dark:text-secondary-400 mt-1">Role: {divRole.role}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800">
                      {divRole.role}
                    </span>
                    {index === 0 && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900 text-green-800">
                        Primary
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <Building className="h-12 w-12 text-gray-400 dark:text-secondary-500 mx-auto mb-3" />
            <p className="text-gray-500 dark:text-secondary-400">No divisions assigned</p>
            <p className="text-sm text-gray-400 dark:text-secondary-500 mt-2">
              Contact your administrator to be assigned to a division
            </p>
          </div>
        )}
      </Card>
    </div>
  );
}

interface ActivityInformationProps {
  userActivity: UserActivityDto | null;
  isLoading: boolean;
}

function ActivityInformation({ userActivity, isLoading }: ActivityInformationProps) {
  if (isLoading) {
    return (
      <Card>
        <div className="flex items-center justify-center py-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-secondary-400">Loading activity...</p>
          </div>
        </div>
      </Card>
    );
  }

  if (!userActivity) {
    return (
      <Card>
        <div className="text-center py-8">
          <Activity className="h-12 w-12 text-gray-400 dark:text-secondary-500 mx-auto mb-3" />
          <p className="text-gray-500 dark:text-secondary-400">Activity information not available</p>
          <p className="text-sm text-gray-400 dark:text-secondary-500 mt-2">
            Activity tracking may not be enabled for your account
          </p>
        </div>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Activity Stats */}
      <Card>
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
            <Activity className="h-5 w-5 mr-2 text-primary-600" />
            Activity Summary
          </h3>
        </div>
        <div className="space-y-4">
          <div className="flex justify-between items-center p-3 bg-gray-50 dark:bg-secondary-900 rounded-lg">
            <span className="text-sm font-medium text-gray-700 dark:text-secondary-200">Total Orders</span>
            <span className="text-lg font-bold text-gray-900 dark:text-white">{userActivity.totalOrders}</span>
          </div>
          <div className="flex justify-between items-center p-3 bg-blue-50 dark:bg-blue-950 rounded-lg">
            <span className="text-sm font-medium text-blue-700">Pending Approvals</span>
            <span className="text-lg font-bold text-blue-900">{userActivity.pendingApprovals}</span>
          </div>
          <div className="flex justify-between items-center p-3 bg-green-50 dark:bg-green-950 rounded-lg">
            <span className="text-sm font-medium text-green-700 dark:text-green-400">Completed Approvals</span>
            <span className="text-lg font-bold text-green-900">{userActivity.completedApprovals}</span>
          </div>
        </div>
      </Card>

      {/* Recent Activity */}
      <Card>
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
            <Calendar className="h-5 w-5 mr-2 text-primary-600" />
            Session Information
          </h3>
        </div>
        <div className="space-y-4">
          {userActivity.lastLoginAt && (
            <div>
              <label className="text-sm font-medium text-gray-500 dark:text-secondary-400">Last Login</label>
              <p className="mt-1 text-gray-900 dark:text-white">
                {format(new Date(userActivity.lastLoginAt), 'PPpp')}
              </p>
            </div>
          )}
          {userActivity.lastActivityAt && (
            <div>
              <label className="text-sm font-medium text-gray-500 dark:text-secondary-400">Last Activity</label>
              <p className="mt-1 text-gray-900 dark:text-white">
                {format(new Date(userActivity.lastActivityAt), 'PPpp')}
              </p>
            </div>
          )}
          {!userActivity.lastLoginAt && !userActivity.lastActivityAt && (
            <p className="text-gray-500 dark:text-secondary-400 text-sm">No session information available</p>
          )}
        </div>
      </Card>

      {/* Recent Activity Timeline */}
      {userActivity.recentActivity && userActivity.recentActivity.length > 0 && (
        <Card className="lg:col-span-2">
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Recent Activity Timeline
            </h3>
          </div>
          <div className="space-y-2">
            {userActivity.recentActivity.map((activity, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 bg-gray-50 dark:bg-secondary-900 rounded-lg"
              >
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-primary-500 rounded-full"></div>
                  <span className="text-sm font-medium text-gray-700 dark:text-secondary-200">{activity.activityType}</span>
                </div>
                <div className="flex items-center space-x-4">
                  <span className="text-sm text-gray-500 dark:text-secondary-400">
                    {format(new Date(activity.date), 'PP')}
                  </span>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-200 dark:bg-secondary-700 text-gray-800 dark:text-secondary-100">
                    {activity.count}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

interface MatchIdentifiersInformationProps {
  matchIdentifiersData: { userId: string; matchIdentifiers: UserMatchIdentifierDto[]; activeCount: number } | null;
  isLoading: boolean;
  divisionId?: string;
}

function MatchIdentifiersInformation({ matchIdentifiersData, isLoading, divisionId }: MatchIdentifiersInformationProps) {
  if (isLoading) {
    return (
      <Card>
        <div className="flex items-center justify-center py-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-secondary-400">Loading match identifiers...</p>
          </div>
        </div>
      </Card>
    );
  }

  if (!divisionId) {
    return (
      <Card>
        <div className="text-center py-8">
          <Tag className="h-12 w-12 text-gray-400 dark:text-secondary-500 mx-auto mb-3" />
          <p className="text-gray-500 dark:text-secondary-400">No division assigned</p>
          <p className="text-sm text-gray-400 dark:text-secondary-500 mt-2">
            You need to be assigned to a division to view match identifiers
          </p>
        </div>
      </Card>
    );
  }

  if (!matchIdentifiersData || matchIdentifiersData.matchIdentifiers.length === 0) {
    return (
      <Card>
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
            <Tag className="h-5 w-5 mr-2 text-primary-600" />
            Match Identifiers
          </h3>
          <p className="text-sm text-gray-500 dark:text-secondary-400 mt-1">
            Match identifiers help route orders to you automatically based on specific criteria
          </p>
        </div>
        <div className="text-center py-8">
          <Search className="h-12 w-12 text-gray-400 dark:text-secondary-500 mx-auto mb-3" />
          <p className="text-gray-500 dark:text-secondary-400">No match identifiers configured</p>
          <p className="text-sm text-gray-400 dark:text-secondary-500 mt-2">
            Contact your administrator to assign match identifiers to your account
          </p>
        </div>
      </Card>
    );
  }

  const autoMatchIdentifiers = matchIdentifiersData.matchIdentifiers.filter(mi => mi.autoMatch);
  const manualMatchIdentifiers = matchIdentifiersData.matchIdentifiers.filter(mi => !mi.autoMatch);

  return (
    <div className="space-y-6">
      {/* Summary Card */}
      <Card>
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
            <Tag className="h-5 w-5 mr-2 text-primary-600" />
            Match Identifiers Overview
          </h3>
          <p className="text-sm text-gray-500 dark:text-secondary-400 mt-1">
            Match identifiers help route orders to you based on specific criteria extracted from order data
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 bg-primary-50 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-primary-700">Total Configured</span>
              <span className="text-2xl font-bold text-primary-900">{matchIdentifiersData.matchIdentifiers.length}</span>
            </div>
          </div>
          <div className="p-4 bg-green-50 dark:bg-green-950 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-green-700 dark:text-green-400">Auto-Match</span>
              <span className="text-2xl font-bold text-green-900">{autoMatchIdentifiers.length}</span>
            </div>
          </div>
          <div className="p-4 bg-blue-50 dark:bg-blue-950 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-blue-700">Manual Only</span>
              <span className="text-2xl font-bold text-blue-900">{manualMatchIdentifiers.length}</span>
            </div>
          </div>
        </div>
      </Card>

      {/* Auto-Match Enabled Identifiers */}
      {autoMatchIdentifiers.length > 0 && (
        <Card>
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
              <CheckCircle className="h-5 w-5 mr-2 text-green-600 dark:text-green-400" />
              Auto-Match Enabled
            </h3>
            <p className="text-sm text-gray-500 dark:text-secondary-400 mt-1">
              These identifiers automatically route matching orders to you without manual approval
            </p>
          </div>
          <div className="space-y-3">
            {autoMatchIdentifiers.map((identifier) => (
              <div
                key={identifier.id}
                className="p-4 border border-green-200 dark:border-green-800 rounded-lg bg-green-50 dark:bg-green-950"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <h4 className="font-semibold text-gray-900 dark:text-white">{identifier.matchIdentifierName}</h4>
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900 text-green-800">
                        Auto-Match
                      </span>
                    </div>
                    <div className="mt-2 space-y-1">
                      <div className="flex items-center text-sm">
                        <span className="font-medium text-gray-600 dark:text-secondary-400 w-24">JSON Path:</span>
                        <code className="text-gray-900 dark:text-white bg-white dark:bg-secondary-800 px-2 py-0.5 rounded border border-gray-200 dark:border-secondary-700">
                          {identifier.jsonPath}
                        </code>
                      </div>
                      {identifier.value && (
                        <div className="flex items-center text-sm">
                          <span className="font-medium text-gray-600 dark:text-secondary-400 w-24">Value:</span>
                          <code className="text-gray-900 dark:text-white bg-white dark:bg-secondary-800 px-2 py-0.5 rounded border border-gray-200 dark:border-secondary-700">
                            {identifier.value}
                          </code>
                        </div>
                      )}
                      {identifier.workflows && identifier.workflows.length > 0 && (
                        <div className="flex items-start text-sm mt-2">
                          <span className="font-medium text-gray-600 dark:text-secondary-400 w-24 flex items-center">
                            <Workflow className="h-3 w-3 mr-1" />
                            Workflows:
                          </span>
                          <div className="flex-1">
                            <div className="flex flex-wrap gap-1">
                              {identifier.workflows.map((workflow) => (
                                <span
                                  key={workflow.workflowId}
                                  className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-white dark:bg-secondary-800 text-gray-700 dark:text-secondary-200 border border-gray-300 dark:border-secondary-600"
                                  title={`Priority: ${workflow.priority}, Weight: ${workflow.weight}${workflow.isRequired ? ', Required' : ''}`}
                                >
                                  {workflow.workflowName}
                                  {workflow.isRequired && (
                                    <span className="ml-1 text-red-600 dark:text-red-400">*</span>
                                  )}
                                </span>
                              ))}
                            </div>
                            {identifier.workflows.some(w => w.isRequired) && (
                              <p className="text-xs text-gray-500 dark:text-secondary-400 mt-1">* Required for workflow</p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="mt-2 text-xs text-gray-500 dark:text-secondary-400">
                      Created {format(new Date(identifier.createdAt), 'PP')}
                      {identifier.updatedAt !== identifier.createdAt && (
                        <> • Updated {format(new Date(identifier.updatedAt), 'PP')}</>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Manual Match Only Identifiers */}
      {manualMatchIdentifiers.length > 0 && (
        <Card>
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
              <Tag className="h-5 w-5 mr-2 text-blue-600 dark:text-blue-400" />
              Manual Match Only
            </h3>
            <p className="text-sm text-gray-500 dark:text-secondary-400 mt-1">
              These identifiers are configured but require manual assignment to orders
            </p>
          </div>
          <div className="space-y-3">
            {manualMatchIdentifiers.map((identifier) => (
              <div
                key={identifier.id}
                className="p-4 border border-blue-200 dark:border-blue-800 rounded-lg bg-blue-50 dark:bg-blue-950"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <h4 className="font-semibold text-gray-900 dark:text-white">{identifier.matchIdentifierName}</h4>
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800">
                        Manual
                      </span>
                    </div>
                    <div className="mt-2 space-y-1">
                      <div className="flex items-center text-sm">
                        <span className="font-medium text-gray-600 dark:text-secondary-400 w-24">JSON Path:</span>
                        <code className="text-gray-900 dark:text-white bg-white dark:bg-secondary-800 px-2 py-0.5 rounded border border-gray-200 dark:border-secondary-700">
                          {identifier.jsonPath}
                        </code>
                      </div>
                      {identifier.value && (
                        <div className="flex items-center text-sm">
                          <span className="font-medium text-gray-600 dark:text-secondary-400 w-24">Value:</span>
                          <code className="text-gray-900 dark:text-white bg-white dark:bg-secondary-800 px-2 py-0.5 rounded border border-gray-200 dark:border-secondary-700">
                            {identifier.value}
                          </code>
                        </div>
                      )}
                      {identifier.workflows && identifier.workflows.length > 0 && (
                        <div className="flex items-start text-sm mt-2">
                          <span className="font-medium text-gray-600 dark:text-secondary-400 w-24 flex items-center">
                            <Workflow className="h-3 w-3 mr-1" />
                            Workflows:
                          </span>
                          <div className="flex-1">
                            <div className="flex flex-wrap gap-1">
                              {identifier.workflows.map((workflow) => (
                                <span
                                  key={workflow.workflowId}
                                  className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-white dark:bg-secondary-800 text-gray-700 dark:text-secondary-200 border border-gray-300 dark:border-secondary-600"
                                  title={`Priority: ${workflow.priority}, Weight: ${workflow.weight}${workflow.isRequired ? ', Required' : ''}`}
                                >
                                  {workflow.workflowName}
                                  {workflow.isRequired && (
                                    <span className="ml-1 text-red-600 dark:text-red-400">*</span>
                                  )}
                                </span>
                              ))}
                            </div>
                            {identifier.workflows.some(w => w.isRequired) && (
                              <p className="text-xs text-gray-500 dark:text-secondary-400 mt-1">* Required for workflow</p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="mt-2 text-xs text-gray-500 dark:text-secondary-400">
                      Created {format(new Date(identifier.createdAt), 'PP')}
                      {identifier.updatedAt !== identifier.createdAt && (
                        <> • Updated {format(new Date(identifier.updatedAt), 'PP')}</>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Information Card */}
      <Card className="bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800">
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0">
            <Tag className="h-5 w-5 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <h4 className="font-semibold text-blue-900">About Match Identifiers</h4>
            <p className="text-sm text-blue-700 mt-1">
              Match identifiers use JSONPath expressions to extract specific values from incoming orders.
              When an order's extracted value matches your configured identifier, the order is automatically
              routed to you for approval. This ensures that orders relevant to your area of responsibility
              are directed to the right approver.
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
}

